/**
 * Async gatherer for the "Scheda riepilogo ricevimento merce" export (T-182).
 *
 * Pulls everything the firma/hash block needs straight from the client (all
 * sources are RLS-readable by an authenticated tenant member — see the T-182
 * spec for the policy audit), resolves operator names via `staff_profiles`
 * (`user_profiles` is self-only), downloads the original scan bytes, and returns
 * a fully-resolved `ReceiptModel` + `ReceiptScan` ready for `buildReceiptPdf`.
 *
 * Strictly read-only over the append-only `events`/`event_anchors` tables.
 */
import { getSupabase } from '../supabase.js';
import {
  pickStockIn,
  pickConformity,
  shapeCarico,
  shapeConformity,
  sealEvents,
  eventRange,
  buildNameResolver,
  type ReceiptEvent,
} from './receiptModel.js';
import type { ReceiptModel, ReceiptLine, ReceiptScan } from './receiptPdf.js';

export interface ReceiptInput {
  source: 'fiscal' | 'public';
  docId: string;
  docTypeLabel: string;
  docNumber: string;
  docDateIso: string;
  docStatus: string;
  totalAmount: number | null;
  currency: string;
  supplier: { name: string; piva: string | null; address: string | null; category: string | null } | null;
  lines: ReceiptLine[];
  signedUrl: string | null;
  signedIsPdf: boolean;
  tenantId: string | null;
  // Sede registrata sul documento. `null` significa "sede storica ignota" e
  // non autorizza un fallback inventato; solo `undefined` mantiene il fallback
  // legacy per gli altri chiamanti che non hanno ancora la coordinata.
  businessId?: string | null;
  self: { id: string | null; fullName: string | null };
}

interface EventRow {
  server_rev: number;
  kind: string;
  actor_user_id: string | null;
  role: string | null;
  client_ts: string;
  prev_hash: string | null;
  self_hash: string | null;
  payload: Record<string, unknown> | null;
}

export async function gatherReceiptData(
  input: ReceiptInput,
): Promise<{ model: ReceiptModel; scan: ReceiptScan | null }> {
  const sb = getSupabase();

  // 1) Events linked to this document (carico + conformità), tenant-scoped by RLS.
  const { data: rawEvents } = await sb
    .from('events')
    .select('server_rev,kind,actor_user_id,role,client_ts,prev_hash,self_hash,payload')
    .in('kind', ['stock_in', 'conformity_check'])
    .eq('payload->>document_id', input.docId)
    .order('server_rev', { ascending: true });

  const events: ReceiptEvent[] = ((rawEvents ?? []) as EventRow[]).map((e) => ({
    server_rev: Number(e.server_rev),
    kind: e.kind,
    actor_user_id: e.actor_user_id,
    role: e.role,
    client_ts: e.client_ts,
    prev_hash: e.prev_hash,
    self_hash: e.self_hash,
    payload: e.payload ?? {},
  }));

  const stockIns = pickStockIn(events);
  const conformityEvent = pickConformity(events);
  const receiptEvents = conformityEvent
    ? [...stockIns, conformityEvent]
    : stockIns;

  // 2) Resolve operator display names (staff_profiles is tenant-readable;
  //    user_profiles is self-only, so we use it only for the current user).
  const actorIds = new Set<string>();
  for (const e of receiptEvents) if (e.actor_user_id) actorIds.add(e.actor_user_id);
  if (conformityEvent) {
    const cb = (conformityEvent.payload as { checked_by_user_id?: string }).checked_by_user_id;
    if (cb) actorIds.add(cb);
  }
  const staffNames = new Map<string, string>();
  if (actorIds.size > 0) {
    // Explicitly scope to the document's tenant (a user can have a different
    // display_name per tenant); RLS would also enforce this, but the explicit
    // filter resolves names from the CORRECT tenant for multi-tenant users.
    let query = sb.from('staff_profiles').select('user_id,display_name').in('user_id', [...actorIds]);
    if (input.tenantId) query = query.eq('tenant_id', input.tenantId);
    const { data: staff } = await query;
    for (const s of (staff ?? []) as Array<{ user_id: string; display_name: string }>) {
      if (s.display_name) staffNames.set(s.user_id, s.display_name);
    }
    // T-193: enrich with the AUTHORITATIVE legal name (user_profiles.full_name)
    // for any operator that shares a tenant with the caller. `user_profiles` is
    // self-only under RLS, so without this the consultant/owner exporting a
    // client's document — or any EXISTING document whose operator isn't in
    // staff_profiles — would have no name and fall back to the role ("Ristoratore"),
    // which is invalid for an ASL inspection. The RPC is tenant-scoped (co-members
    // only). full_name wins over staff display_name (legal name on the firma).
    // Defensive: if the RPC isn't deployed yet, degrade silently to staff/self.
    try {
      const { data: tnames } = await sb.rpc('resolve_tenant_member_names', { p_user_ids: [...actorIds] });
      for (const r of (tnames ?? []) as Array<{ user_id: string; full_name: string }>) {
        if (r.full_name?.trim()) staffNames.set(r.user_id, r.full_name.trim());
      }
    } catch {
      /* RPC not deployed yet → keep staff_profiles/self resolution */
    }
  }
  const nameOf = buildNameResolver(staffNames, input.self);

  // 3) Shape carico + conformità + integrity seals + event range.
  const carico = await shapeCarico(stockIns, nameOf);
  const conformity = shapeConformity(conformityEvent, nameOf);
  const documentSeal = await sealEvents(receiptEvents);
  const range = eventRange(receiptEvents);

  // 4) RFC3161 anchor covering the most legally-relevant event (conformity, else
  //    the latest carico rev). Only reported when actually found — never faked.
  const targetRev = conformity.rev ?? carico.revMax ?? 0;
  let anchor: ReceiptModel['anchor'] = null;
  if (input.tenantId && targetRev > 0) {
    const { data: a } = await sb
      .from('event_anchors')
      .select('tsa_provider,anchored_at')
      .eq('tenant_id', input.tenantId)
      .lte('min_rev', targetRev)
      .gte('max_rev', targetRev)
      .order('anchored_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (a) {
      const row = a as { tsa_provider: string; anchored_at: string };
      anchor = { provider: row.tsa_provider, anchoredAtIso: row.anchored_at };
    }
  }

  // 5) Header / footer compliance sources (all tenant-readable).
  let tenantName = '';
  let tenantPiva: string | null = null;
  let responsabileHaccp: string | null = null;
  let businessName: string | null = null;
  let manualRevision: ReceiptModel['manualRevision'] = null;
  if (input.tenantId) {
    const [{ data: tenant }, { data: biz }, { data: manual }] = await Promise.all([
      sb.from('tenants').select('name,piva,responsabile_haccp_name').eq('id', input.tenantId).maybeSingle(),
      input.businessId != null
        ? sb.from('businesses').select('name').eq('id', input.businessId).maybeSingle()
        : input.businessId === undefined
          ? sb
            .from('businesses')
            .select('name')
            .eq('tenant_id', input.tenantId)
            .is('archived_at', null)
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      sb
        .from('manual_revisions')
        .select('revision_number,signed_at')
        .eq('tenant_id', input.tenantId)
        .eq('status', 'signed')
        .order('signed_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);
    if (tenant) {
      const t = tenant as { name?: string; piva?: string | null; responsabile_haccp_name?: string | null };
      tenantName = t.name ?? '';
      tenantPiva = t.piva ?? null;
      responsabileHaccp = t.responsabile_haccp_name ?? null;
    }
    if (biz) businessName = (biz as { name?: string }).name ?? null;
    if (manual) {
      const m = manual as { revision_number?: string; signed_at?: string };
      if (m.revision_number && m.signed_at) {
        manualRevision = { number: m.revision_number, signedAtIso: m.signed_at };
      }
    }
  }

  // 6) Original scan bytes + format.
  const scan = await fetchScan(input.signedUrl, input.signedIsPdf);

  const model: ReceiptModel = {
    generatedAtIso: new Date().toISOString(),
    tenantName,
    businessName,
    tenantPiva,
    responsabileHaccp,
    manualRevision,
    docTypeLabel: input.docTypeLabel,
    docNumber: input.docNumber,
    docDateIso: input.docDateIso,
    docStatus: input.docStatus,
    totalAmount: input.totalAmount,
    currency: input.currency,
    supplier: input.supplier,
    lines: input.lines,
    carico,
    conformity,
    documentSeal,
    eventCount: range.count,
    eventMinRev: range.minRev,
    eventMaxRev: range.maxRev,
    anchor,
  };

  return { model, scan };
}

async function fetchScan(signedUrl: string | null, signedIsPdf: boolean): Promise<ReceiptScan | null> {
  if (!signedUrl) return null;
  try {
    const resp = await fetch(signedUrl);
    if (!resp.ok) return null;
    const buf = new Uint8Array(await resp.arrayBuffer());
    if (buf.length === 0) return null;
    if (isPdf(buf) || signedIsPdf) return { bytes: buf, format: 'pdf' };
    if (isJpg(buf)) return { bytes: buf, format: 'jpg' };
    if (isPng(buf)) return { bytes: buf, format: 'png' };
    // Unknown image format (webp/heic/…): re-encode to JPEG via canvas.
    const jpg = await reencodeToJpeg(buf);
    return jpg ? { bytes: jpg, format: 'jpg' } : null;
  } catch {
    return null;
  }
}

function isPdf(b: Uint8Array): boolean {
  return b.length >= 4 && b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46;
}
function isJpg(b: Uint8Array): boolean {
  return b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff;
}
function isPng(b: Uint8Array): boolean {
  return b.length >= 4 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47;
}

async function reencodeToJpeg(bytes: Uint8Array): Promise<Uint8Array | null> {
  if (typeof document === 'undefined') return null;
  try {
    const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    const blob = new Blob([ab]);
    const url = URL.createObjectURL(blob);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    URL.revokeObjectURL(url);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    const b64 = dataUrl.split(',')[1];
    if (!b64) return null;
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}
